import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { CgSoftwareDownload, CgSoftwareUpload } from 'react-icons/cg';
import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { StyledButton } from '@app/components/button/Button';
import useModal from '@app/hooks/useModal';
import { useNodesEdges } from '../../contexts/useNodesEdges';
import FlowActionsDropwdown from './FlowActionsDropdown';
import SaveFlowModal from '../modals/SaveFlowModal';
import useFlowActions from '@app/hooks/flows/useFlowActions';
import { exportJSON, createSchema } from '../../utils/export-json-flow';
import { importJSON } from '../../utils/import-json-flow';
import { url } from '@app/utils/constants';

type FlowActionBtnsProps = {
    id?: string;
    name: string | undefined;
    type: 'Add' | 'Edit';
    isLoading: boolean;
};

type SaveFlowModalData = {
    title: string;
    onOkBtnTxt: 'Export' | 'Save';
    initialValues: { name: string };
    onOk: (name: string) => void;
};

type ActionType = 'Save' | 'Export' | 'Import' | 'Clear';

const FlowActionBtns = ({ id = '', name = '', type, isLoading }: FlowActionBtnsProps) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [importing, setImporting] = useState(false);
    const navigate = useNavigate();
    const {
        nodes,
        setNodes,
        edges,
        setEdges,
        contextVariables,
        setContextVariables,
        flowOrder,
        totalOrderedFlows,
        incompleteNodeIds,
        resetNodesEdges,
    } = useNodesEdges();
    const { addFlow, editFlow } = useFlowActions();

    const saveModal = useModal<SaveFlowModalData>({});

    const notSingleFlow = totalOrderedFlows !== 1;
    const incompleteNodes = incompleteNodeIds.length > 0;
    const disconnectedNodes = flowOrder.length !== nodes.length;
    const incorrectFlowOrder = flowOrder[0]?.number !== '1';

    const isSaveDisabled = useMemo(
        () =>
            isLoading ||
            notSingleFlow ||
            incompleteNodes ||
            disconnectedNodes ||
            incorrectFlowOrder,
        [isLoading, notSingleFlow, incompleteNodes, disconnectedNodes, incorrectFlowOrder]
    );
    const isExportDisabled = isSaveDisabled || isLoading;
    const isImportDisabled = !!nodes.length || isLoading;
    const isClearDisabled = !nodes.length || isLoading;
    const showExport = Boolean(nodes.length);

    const handleExport = (fileName: string) => {
        try {
            exportJSON(fileName, flowOrder, nodes, edges, contextVariables);
            closeModal();
        } catch (error) {
            console.error('Error exporting JSON:', error);
        }
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        setImporting(true);
        const file = event.target.files?.[0];
        if (file) {
            try {
                await importJSON(file, setNodes, setEdges, setContextVariables);
                console.log('Import complete!');
            } catch (error) {
                console.error('Import failed:', error);
            } finally {
                setImporting(false);
                event.target.value = '';
            }
        }
    };

    const handleSave = (name: string) => {
        const flowJSON = createSchema(name, flowOrder, nodes, edges, contextVariables);
        const schemaObj = { name, schema: flowJSON };

        if (type === 'Edit') {
            editFlow.mutate(
                { id: id, schema: schemaObj },
                {
                    onSuccess: () => {
                        closeModal();
                        navigate(url.adminPortal.internalAdmin.agentFlows.list);
                    },
                }
            );
        } else {
            addFlow.mutate(schemaObj, {
                onSuccess: () => {
                    closeModal();
                    navigate(url.adminPortal.internalAdmin.agentFlows.list);
                },
            });
        }
    };

    const openModal = (modalType: 'Save' | 'Export') => {
        const initialValues = { name: type === 'Edit' ? name || '' : '' };
        if (modalType === 'Export') {
            saveModal.open({
                initialValues: initialValues,
                onOk: handleExport,
                onOkBtnTxt: modalType,
                title: 'Export AI Flow',
            });
        } else {
            saveModal.open({
                initialValues: initialValues,
                onOk: handleSave,
                onOkBtnTxt: modalType,
                title: `${type} AI Flow`,
            });
        }
    };

    const closeModal = () => {
        saveModal.reset();
    };

    const onActionClick = (action: ActionType) => {
        if (action === 'Import') {
            fileInputRef.current?.click();
        } else if (action === 'Clear') {
            resetNodesEdges();
        } else {
            openModal(action);
        }
    };

    return (
        <>
            <StyledButtonGroup justify="flex-start" className="margin-top-0 flow-action-btns">
                <StyledButton
                    type="primary"
                    disabled={isSaveDisabled}
                    onClick={() => onActionClick('Save')}>
                    Save
                </StyledButton>
                {showExport ? (
                    <StyledButton
                        type="primary"
                        icon={<CgSoftwareDownload size={20} />}
                        onClick={() => onActionClick('Export')}
                        disabled={isExportDisabled}>
                        Export JSON
                    </StyledButton>
                ) : (
                    <StyledButton
                        type="primary"
                        icon={<CgSoftwareUpload size={20} />}
                        onClick={() => onActionClick('Import')}
                        disabled={isImportDisabled}
                        loading={importing}>
                        Import JSON
                    </StyledButton>
                )}
                <StyledButton onClick={() => onActionClick('Clear')} disabled={isClearDisabled}>
                    Clear
                </StyledButton>
            </StyledButtonGroup>
            <FlowActionsDropwdown
                showExport={showExport}
                handleBtnClick={onActionClick}
                isSaveDisabled={isSaveDisabled}
                isClearDisabled={isClearDisabled}
                isImportExportDisabled={showExport ? isExportDisabled : isImportDisabled}
            />
            {saveModal.isOpen && (
                <SaveFlowModal
                    open={saveModal.isOpen}
                    data={
                        saveModal.data ?? {
                            title: '',
                            onOkBtnTxt: 'Save',
                            initialValues: { name: '' },
                            onOk: () => {},
                        }
                    }
                    onCancel={closeModal}
                />
            )}
            {Boolean(!nodes.length) && (
                <input
                    type="file"
                    accept=".json,.txt,.app"
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                />
            )}
        </>
    );
};
export default FlowActionBtns;
